import {
  Badge,
  Button,
  Checkbox,
  Heading,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../api";
import { Autobooking } from "../../api/types";
import useFetchAutobookings from "./hooks/useFetchAutobookings";
import useShowToast from "../../hooks/useShowToast";
import SettingsAutomaticBookingsModal from "./settings-automatic-bookings-modal";
import ConfirmModal from "../ui/confirm-modal";

const SettingsAutomaticBookings = (): ReactElement => {
  const { t } = useTranslation();
  const { successToast, errorToast } = useShowToast();
  const { autobookings, refetchAutobookings } = useFetchAutobookings();
  const [autobookingToDelete, setAutobookingToDelete] = useState<Autobooking | undefined>(
    undefined,
  );
  const [autobookingToEdit, setAutobookingToEdit] = useState<Autobooking | undefined>(undefined);

  const {
    isOpen: isAutobookingModalOpen,
    onOpen: onAutobookingModalOpen,
    onClose: onAutobookingModalClose,
  } = useDisclosure();

  const {
    isOpen: isConfirmModalOpen,
    onOpen: onConfirmModalOpen,
    onClose: onConfirmModalClose,
  } = useDisclosure();

  const openModal = () => {
    setAutobookingToEdit(undefined);
    onAutobookingModalOpen();
  };

  const openEditModal = (autobooking: Autobooking) => {
    setAutobookingToEdit(autobooking);
    onAutobookingModalOpen();
  };

  const onModalClose = () => {
    refetchAutobookings();
    setAutobookingToEdit(undefined);
    onAutobookingModalClose();
  };

  const handleDeleteClick = (autobooking: Autobooking) => {
    setAutobookingToDelete(autobooking);
    onConfirmModalOpen();
  };

  const confirmDelete = () => {
    if (autobookingToDelete) {
      handleDelete(autobookingToDelete);
    }
    setAutobookingToDelete(undefined);
    onConfirmModalClose();
  };

  const handleToggleActive = async (autobooking: Autobooking) => {
    try {
      await api.updateAutobooking(autobooking.id, { is_active: !autobooking.is_active });

      if (api.error) {
        errorToast({
          title: t("settings.automatic_bookings.error_title"),
          description: t("settings.automatic_bookings.error_message.default"),
        });
        return;
      }

      refetchAutobookings();
      successToast({
        title: t("settings.automatic_bookings.success_title"),
        description: t("settings.automatic_bookings.success_message"),
      });
    } catch (error) {
      errorToast({
        title: t("settings.automatic_bookings.error_title"),
        description: t("settings.automatic_bookings.error_message.default"),
      });
    }
  };

  const handleDelete = async (autobooking: Autobooking) => {
    try {
      await api.deleteAutobooking(autobooking.id);

      if (api.error) {
        errorToast({
          title: t("settings.automatic_bookings.error_title"),
          description: t("settings.automatic_bookings.error_message.default"),
        });
        return;
      }

      refetchAutobookings();
      successToast({
        title: t("settings.automatic_bookings.modal.delete_success_title"),
        description: t("settings.automatic_bookings.modal.delete_success_message"),
      });
    } catch (error) {
      errorToast({
        title: t("settings.automatic_bookings.error_title"),
        description: t("settings.automatic_bookings.error_message.default"),
      });
    }
  };

  const getDayName = (day: number): string => t(`settings.automatic_bookings.days.${day}`);

  return (
    <>
      <Heading size="lg" mb={4}>
        {t("settings.automatic_bookings.title")}
      </Heading>

      <TableContainer>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>{t("settings.automatic_bookings.day")}</Th>
              <Th>{t("settings.automatic_bookings.start_time")}</Th>
              <Th>{t("settings.automatic_bookings.number_of_hours")}</Th>
              <Th>{t("settings.automatic_bookings.user")}</Th>
              <Th>{t("settings.automatic_bookings.court")}</Th>
              <Th>{t("settings.automatic_bookings.is_double")}</Th>
              <Th>{t("settings.automatic_bookings.is_active")}</Th>
              <Th> </Th>
            </Tr>
          </Thead>
          <Tbody>
            {autobookings.map(autobooking => (
              <Tr key={autobooking.id}>
                <Td pb={1} pt={1}>
                  {getDayName(autobooking.day)}
                </Td>
                <Td pb={1} pt={1}>
                  {autobooking.start_time.slice(0, 5)}
                </Td>
                <Td pb={1} pt={1}>
                  {autobooking.number_of_hours}
                </Td>
                <Td pb={1} pt={1}>
                  {autobooking.user.username}
                  {autobooking.user.is_service ? (
                    <Badge ml={2} colorScheme="yellow" fontWeight="normal" textTransform="none">
                      {t("settings.automatic_bookings.service_badge")}
                    </Badge>
                  ) : (
                    autobooking.user.is_guest && (
                      <Badge ml={2} colorScheme="gray" fontWeight="normal" textTransform="none">
                        {t("settings.automatic_bookings.guest_badge")}
                      </Badge>
                    )
                  )}
                </Td>
                <Td pb={1} pt={1}>
                  {autobooking.court.name}
                </Td>
                <Td pb={1} pt={1}>
                  {autobooking.is_double ? "Sì" : "No"}
                </Td>
                <Td pb={1} pt={1}>
                  <Checkbox
                    isChecked={autobooking.is_active}
                    onChange={() => handleToggleActive(autobooking)}
                  />
                </Td>
                <Td pb={1} pt={1}>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => openEditModal(autobooking)}
                    mr={2}
                  >
                    {t("settings.automatic_bookings.edit")}
                  </Button>
                  <Button
                    colorScheme="red"
                    size="xs"
                    onClick={() => handleDeleteClick(autobooking)}
                  >
                    {t("settings.automatic_bookings.delete")}
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      <Button mt={4} colorScheme="blue" onClick={() => openModal()}>
        {t("settings.automatic_bookings.add_autobooking")}
      </Button>

      <SettingsAutomaticBookingsModal
        isOpen={isAutobookingModalOpen}
        onClose={onModalClose}
        autobooking={autobookingToEdit}
      />

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={onConfirmModalClose}
        onConfirm={confirmDelete}
        title={t("settings.automatic_bookings.modal.delete_title")}
        message={t(
          "settings.automatic_bookings.modal.delete_message",
          autobookingToDelete
            ? {
                day: getDayName(autobookingToDelete.day),
                time: autobookingToDelete.start_time.slice(0, 5),
                user: autobookingToDelete.user.username,
                court: autobookingToDelete.court.name,
              }
            : undefined,
        )}
        confirmText={t("settings.automatic_bookings.modal.delete")}
        colorScheme="red"
      />
    </>
  );
};

export default SettingsAutomaticBookings;
